import { id, se } from "date-fns/locale";
import { FormSectionElements, FormSections, FormSubSections, ProgramContainer } from "../entities/formTemplateDataType";

export const getParentSection = (element: FormSectionElements): FormSections | undefined => {
    if (element) {
        let section = element.parent;
        while(section && !(section instanceof FormSections)) {
            section = section.parent; 
        }
        if (section) {
            return section;
        }
    }
    return undefined;
}

export const getRootComponent = (field: FormSectionElements | FormSubSections | FormSections): ProgramContainer => {
    let section = field.parent;
    while(section && !section.ListOfFormSections) {
        section = section.parent;
    }
    return section;
}

export const isFirstElement = (element: FormSectionElements): boolean => {
    if (element) {
        const index = element.parent.ListOfFormSectionElements.findIndex(ele => ele.Id === element.Id);
        return index === 0;
    }
    return false;
};

export const isLastElement = (element: FormSectionElements): boolean => {
    if (element) {
        const elements = element.parent.ListOfFormSectionElements;
        const index = elements.findIndex(ele => ele.Id === element.Id);
        return index === (elements.length -1);
    }
    return false;
};

export const getElementByFieldName = (fieldName: string, formSection: FormSections): FormSectionElements => {
    const root = formSection.parent;

    const formList = root.ListOfFormSections;
    let eleReturned;
    try {
        formList.forEach(section => {
            section.ListOfFormSubSections.forEach(sub => {
                const elements = sub.ListOfFormSectionElements;
                elements.forEach(ele => {
                    if (ele.LoadBindName == fieldName) {
                        eleReturned = ele;
                        throw new Error('Exit all loops');
                    }
                })
            })
        });
    } catch {
        return eleReturned;
    }

    return null;
};

export const getNearstParent = (element: HTMLElement, parentName: string) => {
    if (!parentName) return null;
    let parent = element.parentElement;
    while (parent && parent.nodeName.toLocaleLowerCase() !== parentName.toLocaleLowerCase()) {
        parent = parent.parentElement;
    }
    return parent;
};

export const scrollToContentHeader = (element: HTMLElement) => {
    const h1 = getNearstParent(element, 'h1');
    if (h1) {
        h1.focus();
    }
};

export const focusOnSectionHeader = (section: FormSections) => {
    let invalidElement = null;
    let invalidSubsec = null
    let index = 0;
    let h1Ids: string[] = [];

    for (let subSection of section.ListOfFormSubSections) {
        h1Ids = [];
        for (let i=0; i< subSection.ListOfFormSectionElements.length; i++) {
            const element = subSection.ListOfFormSectionElements[i];
            if (!element.Visible) {
                continue;
            }

            if (element.Datatype === 'Label' || element.Datatype === 'HTML') {
                h1Ids.push(section.Id + '-' + subSection.Sequence + '-' + element.Id);
            }
            if (!element.Valid && element.Visible) {
                invalidElement = element;
                invalidSubsec = subSection;
                index = i;
                break;
            }
        }
    }
    
    if (invalidElement && h1Ids.length) {
        const h1Elements = Array.from(h1Ids).map(h1Id => document.querySelector(`div#Label-${h1Id} h1`))
            .filter(Boolean);
        if (h1Elements.length) {
            const h1 = h1Elements.pop() as HTMLHeadingElement;
            h1.focus();
        }
    }
}

export const findMatchedAddressElement = (field: FormSectionElements): FormSectionElements | null => {
    const subSection = field.parent;
    for(let element of subSection.ListOfFormSectionElements) {
        const type = field.addressType === 'Mailing' ? 'Residential' : 'Mailing';
        if (element.Datatype === 'Address' && element.addressType === type) {
            return element;
        }
    }
    return null;
}

export const focusOnFirstErrorElement = () => {
    const errorEle = getFirstErrorElement();
    if (errorEle) {
        (errorEle as HTMLInputElement ).focus();
    };
}

export const getFirstErrorElement = () => {
    const pattern = /ontario-[a-z]+__error/i;
    const elements = document.querySelectorAll('[class*="__error"]');
    return Array.from(elements).filter(ele => pattern.test(ele.className))?.[0];
}


export const getAppId = (field: FormSectionElements | FormSubSections | FormSections): string => {
    const root = getRootComponent(field);
    return root?.CategoryId ?? '';
}

export const isSectionVisible = (section: FormSections, programContainer: ProgramContainer, callback: (p: ProgramContainer) => void): boolean => {
    section.ListOfFormSubSections.forEach(sub => {
        sub.ListOfFormSectionElements.forEach(ele => {
            if (ele.Datatype === 'PublicSecureButton') {
                if (programContainer.Profile?.userId) {
                    if (section.Visible) {
                        section.Visible = false;
                        callback(programContainer);
                    }
                } else {
                    if (!section.Visible) {
                        section.Visible = true;
                        callback(programContainer);
                    }
                }
            }
        });
    });
    return section.Visible;
}

export const setPublicSecurePageVisibility = (field: FormSectionElements, callback: (p: ProgramContainer) => void): void => {
    if (field.Datatype !== 'PublicSecureButton') return;

    const root = getRootComponent(field);
    const url = new URL(window.location.href)
    const code = url.searchParams.get("code");
    const state = url.searchParams.get('state'); 
    
    const section = field.parent.parent;
    if (root.Profile?.userId) {
        if (section.Visible) {
            section.Visible = false;
            callback(root);
        }
    } else if (code && state) {
        if (section.Visible) {
            section.Visible = false;
            callback(root);
        }   
    } else {
        if (!section.Visible) {
            section.Visible = true;
            callback(root);
        }
    }
}

export const hasPublicSecureButton = (section: FormSections): boolean => {
    let hasButton = false;
    section.ListOfFormSubSections.forEach(sub => {
        sub.ListOfFormSectionElements.forEach(ele => {
            if (ele.Datatype === 'PublicSecureButton') {
                hasButton = true;
            }
        });
    });
    return hasButton;
}

export const getPublicSecurePage = (sections: FormSections[]): FormSections => {
    return sections.find(section => hasPublicSecureButton(section));
};

export const needAccessToken = (section: FormSections): boolean => {
    let needToken = section.GetToken === 'Y';
    const hasPublicSecureBtn = hasPublicSecureButton(section);
    if (hasPublicSecureBtn) {
        const root = getRootComponent(section);
        if (!root.Profile?.token) {
            needToken = true;
        }
    }
    return needToken;
}

export const needCxpProfile = (section: FormSections): boolean => {
    let needProfile = false;
    const hasPublicSecureBtn = hasPublicSecureButton(section);
    if (hasPublicSecureBtn) {
        const root = getRootComponent(section);
        if (!root.Profile?.lastName || !root.Profile?.firstName) {
            needProfile = true;
        }
    }
    return needProfile;
}

export const getSubSectionButton = (subSection: FormSubSections): FormSectionElements | null => {
    if (!subSection) return null;
    const buttons = subSection.ListOfFormSectionElements.filter(ele => ele.Datatype === 'Button' && ele.Visible);
    return buttons[0] ?? null;
};

export const getFieldByName = (name: string, field: FormSectionElements): FormSectionElements | undefined => {
    const root = getRootComponent(field);
    let bindField;
    root.ListOfFormSections.forEach(section => {
        section.ListOfFormSubSections.forEach(sub => {
            sub.ListOfFormSectionElements.forEach(ele => {
                if (ele.LoadBindName === name) {
                    bindField = ele;
                }
            });
        });
    });
    return bindField;
}

export const findAddressElement = (summaryField: FormSectionElements) => {
    const root = getRootComponent(summaryField);
    let addressField: FormSectionElements;
    let find = false;
    
    for (const form of root.ListOfFormSections) {
        if (find) break;
        for (const subForm of form.ListOfFormSubSections) {
            const fields = subForm.ListOfFormSectionElements.filter(ele => (
                ele.Datatype==='Address' && (ele.addressType ?? 'Residential') === (summaryField.addressType ?? 'Residential')
            ));

            if (fields.length) {
                addressField = fields[0];
                find = true;
                break;
            }
        }
    }
    return addressField;
}

export const generateSectionIndex = (element: FormSectionElements) => {
    const root = getRootComponent(element);
    const index = root.ListOfFormSections.map(section => {
        return {sequence: section.Sequence, title: section.ENUTitle };
    });
    return index;
};

export const getSectionBySequence = (field: FormSectionElements, sequence: number = undefined) => {
    const root = getRootComponent(field);
    if (sequence) {
        return root.ListOfFormSections.find(section => section.Sequence === sequence);
    }
    return field.parent.parent;
};

export const isEventWithinElement = (event: MouseEvent, element: HTMLElement): boolean => {
    const rect = element.getBoundingClientRect();
    return (
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
    );
};
